// Flange (NetSuite CPQ product code FLANGE, internal id 8).
//
// Shares nothing behavioural with the other configurators. Empty sets and false flags
// below are DECLARATIONS: the shared machinery reads them and does nothing, rather
// than falling back to Natural Draft's answer.
//
// Its export sets item fields DIRECTLY -- both ITCRs carry 36/35 custitem_* fields and
// NO custitem_mapping, so the fixed-width encoder is not involved at all.
//
// NOT YET LAUNCHABLE (registry enabled:false). One blocker, and it is structural
// rather than a data problem:
//
//   DUAL ITEM. This is the first configurator that builds TWO items -- the Flange and,
//   when INCLUDE_GASKET is Yes (or MATERIAL_TYPE is the gasket material), a separate
//   Gasket. It has two ITCRs with DIFFERENT itemid templates ({ID_KEY/ID_KEY} and
//   {ID_KEYG/ID_KEYG}) and its own parallel validation set for each
//   (id_key/display_name/description/sales_description, then gasket_* of each).
//   cpq2's build path is single-item throughout: buildAssembly does itcrs.find(...)
//   and returns one item, and SaveSubmitButton creates one item, one BOM, one routing
//   and one sales-order line. Verified with INCLUDE_GASKET=Yes: BOTH ITCRs match, and
//   the Gasket (FLG-XXXX-X10XX-0) is dropped with no warning.
import type { ProductProfile } from "./profile";

export const FLANGE_PROFILE: ProductProfile = {
  slug: "flange",

  // Nothing is manually hidden. Note WELDED_NUTS and BOLT_HOLE_QUANTITY are also
  // Natural Draft question codes -- two more collisions that a flat, product-blind set
  // of codes would have applied here by mistake.
  hiddenQuestionCodes: new Set(),

  // MAIN is shared with Flame Cells and Pilot, which is exactly why this map is keyed by
  // product and not by tab code alone -- Flame Cells suppresses PRODUCT_SPECIFICATIONS
  // on its MAIN tab and that must not reach this product.
  hiddenGroupCodesByTab: {
    // "Additional Options" holds 7 questions and EVERY ONE of them is unconditionally
    // hidden: ANSI_FLANGE, PRESSURE_RATING, FACE_TYPE, MOUNTING_TYPE and their three *2
    // helpers all carry parsed_hide_rule "true". The GROUP itself has no hide rule, so it
    // rendered as a box containing nothing but Group.tsx's scaffold line "No questions in
    // this group yet.".
    //
    // Same shape as Flame Cells' PRODUCT_SPECIFICATIONS, but arrived at differently: that
    // group has no questions assigned at all, whereas this one has seven that are all
    // hidden. Worth distinguishing, because someone deliberately set each of those four
    // real options to hidden in NetSuite -- so either they are parked until Flange needs
    // them (suppressing the group is then correct and permanent), or the hide rules are
    // wrong and the options are supposed to show, which is a NetSuite-side fix. Kevin has
    // been asked which.
    MAIN: new Set(["ADDITIONAL_OPTIONS"]),
  },

  // No option is narrowed beyond what this product's export already says.
  answerHideRules: [],

  optionalQuestionCodes: new Set(),
  optionalQuestionPairs: [],
  hardRequiredQuestionCodes: new Set(),
  suffixNormalizedFields: new Set(),

  // No FAH concept: that is a Natural Draft burner configuration.
  fahSectionGroupCodes: new Set(),
  // No typeable dropdown here: every list this CPQ shows is short enough to pick from.
  comboBoxQuestionCodes: new Set(),
  // Nothing here may invent a NetSuite list value.
  createsAnswersFor: new Set(),
  // Nothing is auto-created here, and no question is restricted to whole numbers,
  // because this product has no typeable list at all.
  autoCreatesAnswersFor: new Set(),
  wholeNumberQuestionCodes: new Set(),
  unlistedSizeTextFallback: new Map(),
  maxInches: new Map(),

  // No encoded mapping at all, so there is nothing to exempt from mapping-mode nulling.
  mappingHideExemptCodes: new Set(),

  // The PRIMARY (Flange) item's key, matching the first ITCR's {ID_KEY/ID_KEY}. The
  // Gasket ITCR uses {ID_KEYG/ID_KEYG}; that second key has no home until cpq2 can
  // build more than one item per submit.
  fingerprintAnswerKey: "ID_KEY/ID_KEY",

  // Per Kevin: "flange will not have a route". Both of its ITCRs set routing:true, which
  // is simply wrong for this product, and that mismatch was the last thing blocking every
  // Flange submit. With no routing there is nothing to file against the BOM, and
  // hasRoutingSteps in api/create-assembly stops an empty routing record being created.
  buildsRouting: false,

  // Flange's export carries NO material rows, so its BOM came out empty and the
  // missing-components warning blocked every submit. Every other configurator has a
  // single unconditional row pointing at the shared CPQ placeholder item -- Natural Draft
  // "Bom Placeholder", Flame Cells "BOM Placeholder", Pilot "CPQ Place Holder", all item
  // 548487 -- which is what their BOMs actually contain today. This supplies the same
  // one. It is a FALLBACK: add the material row to the Flange product in NetSuite and the
  // ruleset takes over, with no duplicate placeholder.
  bomFallbackComponents: [{ item: "548487", qty: "1" }],

  submit: {
    // Flange is not a burner: no serial tag, no Natural Draft add-on lines, no NDB
    // price list, no Natural Draft customer-requirements record, and none of the
    // custitem_* writes whose value maps are keyed by Natural Draft answer codes.
    serialTag: false,
    additionalLines: false,
    listPricing: false,
    customerRequirements: false,
    ndbItemFields: false,
  },

  // Writes no sales-order body fields.
  orderFields: [],
};
